import React, { useEffect, useRef } from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MessageList = ({ messages = [], currentUserId, typingUser = null }) => {
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'HH:mm', { locale: es });
    }
    if (isYesterday(date)) {
      return `Ayer ${format(date, 'HH:mm', { locale: es })}`;
    }
    return format(date, 'dd MMM HH:mm', { locale: es });
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return 'archivo';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'archivo';
  };

  const getFileExtension = (filename) => {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  };

  const isImage = (filename) => {
    const ext = getFileExtension(filename);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  };

  const getFullFileUrl = (fileUrl) => {
    if (!fileUrl) return '';
    // Si ya es una URL completa, devolverla tal cual
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      return fileUrl;
    }
    // Si es relativa, agregar el backend URL
    return `${BACKEND_URL}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
  };

  const renderDateSeparator = (date) => {
    const messageDate = new Date(date);
    let label = '';
    
    if (isToday(messageDate)) {
      label = 'Hoy';
    } else if (isYesterday(messageDate)) {
      label = 'Ayer';
    } else {
      label = format(messageDate, 'dd MMMM yyyy', { locale: es });
    }

    return (
      <div className="flex justify-center my-4">
        <div className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
          {label}
        </div>
      </div>
    );
  };

  const shouldShowDateSeparator = (currentMsg, prevMsg) => {
    if (!prevMsg) return true;
    
    const currentDate = new Date(currentMsg.created_at);
    const prevDate = new Date(prevMsg.created_at);
    
    return currentDate.toDateString() !== prevDate.toDateString();
  };

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="text-sm">No hay mensajes todavía</p>
          <p className="text-xs mt-1">Envía el primer mensaje para iniciar la conversación</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 'calc(100vh - 240px)' }}>
      {messages.map((message, index) => {
        const isOwnMessage = message.sender_id === currentUserId;
        const showDateSeparator = shouldShowDateSeparator(message, messages[index - 1]);

        return (
          <React.Fragment key={message.id}>
            {showDateSeparator && renderDateSeparator(message.created_at)}
            
            <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : 'order-1'}`}>
                {/* Sender name (only for received messages) */}
                {!isOwnMessage && (
                  <div className="text-xs text-gray-600 mb-1 px-2">
                    {message.sender_name || 'Usuario'}
                  </div>
                )}
                
                {/* Message bubble */}
                <div
                  className={`px-4 py-2 rounded-2xl ${
                    isOwnMessage
                      ? 'bg-gradient-to-r from-cyan-600 to-teal-600 text-white rounded-br-none'
                      : 'bg-gray-800 text-gray-100 rounded-bl-none'
                  }`}
                >
                  {/* File attachment */}
                  {message.message_type === 'file' && message.file_url && (
                    <div className="mb-2">
                      {isImage(message.file_url) ? (
                        // Mostrar imagen
                        <a
                          href={getFullFileUrl(message.file_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          <img
                            src={getFullFileUrl(message.file_url)}
                            alt="Imagen adjunta"
                            className="max-w-xs max-h-64 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onError={(e) => {
                              // Si falla la carga de imagen, mostrar como archivo normal
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                          {/* Fallback si la imagen no carga */}
                          <div 
                            className={`hidden items-center space-x-2 p-3 rounded-lg ${
                              isOwnMessage ? 'bg-blue-700' : 'bg-white'
                            }`}
                          >
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium">📷 {getFileNameFromUrl(message.file_url)}</p>
                              <p className="text-xs opacity-75">Click para ver</p>
                            </div>
                          </div>
                        </a>
                      ) : (
                        // Mostrar archivo no-imagen
                        <a
                          href={getFullFileUrl(message.file_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={getFileNameFromUrl(message.file_url)}
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                            isOwnMessage 
                              ? 'bg-cyan-700 hover:bg-cyan-800' 
                              : 'bg-gray-700 hover:bg-gray-600'
                          }`}
                          onClick={(e) => {
                            // Forzar descarga usando fetch + blob
                            e.preventDefault();
                            const url = getFullFileUrl(message.file_url);
                            const filename = getFileNameFromUrl(message.file_url);
                            
                            fetch(url)
                              .then(response => response.blob())
                              .then(blob => {
                                const blobUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.download = filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(blobUrl);
                              })
                              .catch(err => {
                                console.error('Error downloading file:', err);
                                // Fallback: abrir en nueva pestaña
                                window.open(url, '_blank');
                              });
                          }}
                        >
                          <div className={`p-2 rounded-lg ${isOwnMessage ? 'bg-cyan-800' : 'bg-gray-600'}`}>
                            <svg className={`w-6 h-6 ${isOwnMessage ? 'text-white' : 'text-cyan-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              📎 {getFileNameFromUrl(message.file_url)}
                            </p>
                            <p className={`text-xs ${isOwnMessage ? 'text-cyan-200' : 'text-gray-400'}`}>
                              Click para descargar
                            </p>
                          </div>
                          <svg className={`w-5 h-5 flex-shrink-0 ${isOwnMessage ? 'text-cyan-200' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                  
                  {/* Message content */}
                  {message.content && (
                    <p className="text-sm break-words whitespace-pre-wrap">{message.content}</p>
                  )}
                  
                  {/* Timestamp */}
                  <div className={`text-xs mt-1 ${isOwnMessage ? 'text-cyan-200' : 'text-gray-400'}`}>
                    {formatMessageDate(message.created_at)}
                  </div>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
      
      {/* Typing indicator */}
      {typingUser && (
        <div className="flex justify-start">
          <div className="bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-none">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
